import React, { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { Button, Alert, Modal, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import apiClient from "../api/apiClient";

/* ──────────────────────────── helpers ──────────────────────────── */

const excelDateToJSDate = (serial) => {
    const utc_days = Math.floor(serial - 25569);
    const utc_value = utc_days * 86400;
    const date_info = new Date(utc_value * 1000);
    return date_info.toISOString().slice(0, 10);
};

const fmtDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d) ? v : d.toLocaleDateString("es-PY");
};

/* ═══════════════════════════ COMPONENTE ═══════════════════════════ */

const OrdersServices = () => {
    /* ─── state: tabla principal (órdenes cargadas) ─── */
    const [ordenes, setOrdenes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [error, setError] = useState("");

    /* ─── state: eliminación por rango ─── */
    const [codeIni, setCodeIni] = useState("");
    const [codeFinal, setCodeFinal] = useState("");
    const [deleting, setDeleting] = useState(false);

    /* ─── state: modal de importación ─── */
    const [showImport, setShowImport] = useState(false);
    const [file, setFile] = useState(null);
    const [importData, setImportData] = useState([]);
    const [importFilter, setImportFilter] = useState("");
    const [importError, setImportError] = useState("");
    const [sending, setSending] = useState(false);
    const [importMessage, setImportMessage] = useState("");

    /* ═══════════════════ CARGAR ÓRDENES (SP) ═══════════════════ */

    const fetchOrdenes = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await apiClient.get("/sales-orders/ordenes-servicios");
            setOrdenes(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setError(e?.response?.data?.error || "Error al obtener órdenes de servicios");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOrdenes(); }, [fetchOrdenes]);

    /* ═══════════════════ ELIMINAR POR RANGO ═══════════════════ */

    const handleDelete = async () => {
        if (!codeIni || !codeFinal) {
            alert("Debe ingresar el rango de Code (Desde / Hasta).");
            return;
        }
        if (!window.confirm(`¿Está seguro de eliminar las órdenes del rango ${codeIni} al ${codeFinal}?`)) return;

        setDeleting(true);
        try {
            const res = await apiClient.delete("/sales-orders/ordenes-servicios", {
                params: { codeIni, codeFinal }
            });
            alert(res.data?.message || "Registros eliminados correctamente.");
            setCodeIni("");
            setCodeFinal("");
            fetchOrdenes();
        } catch (e) {
            alert(e?.response?.data?.message || e?.response?.data?.error || "Error al eliminar");
        } finally {
            setDeleting(false);
        }
    };

    /* ═══════════════════ MODAL IMPORTACIÓN ═══════════════════ */

    const openImport = () => {
        setFile(null);
        setImportData([]);
        setImportFilter("");
        setImportError("");
        setImportMessage("");
        setShowImport(true);
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setImportData([]);
        setImportError("");
        setImportFilter("");
        setImportMessage("");
    };

    const handleReadFile = () => {
        if (!file) { setImportError("Por favor, selecciona un archivo Excel."); return; }
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = (ev) => {
            const wb = XLSX.read(new Uint8Array(ev.target.result), { type: "array" });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            setImportData(XLSX.utils.sheet_to_json(sheet));
        };
        reader.onerror = () => setImportError("Error al leer el archivo.");
    };

    const exportErrorsToExcel = (results = []) => {
        const errs = (results || []).filter(r => r.status === "error");
        if (!errs.length) return;
        const ws = XLSX.utils.json_to_sheet(errs.map(item => {
            const r = item.registro;
            return {
                Código: r.U_CntctCode, Servicio: r.U_TipoServ, Tipo: r.U_CardCode,
                Nivel: r.U_Nivel, NroOS: r.U_NroOS, Lote: r.U_Lote,
                Cantidad: r.U_Quantity, Inicial: r.U_Inicial, Final: r.U_Final,
                Detalle: item.detalle
            };
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Errores");
        XLSX.writeFile(wb, "Errores_OrdenesServicios.xlsx");
    };

    const handleSendToBackend = async () => {
        if (!importData.length) { setImportError("No hay datos para enviar."); return; }
        setSending(true);
        setImportMessage("");
        try {
            const datosFormateados = importData.map(item => ({
                U_CntctCode: item["Codigo"],
                U_Quantity: item["Cantidad"],
                U_TipoServ: item["Servicio"],
                U_Lote: item["Lote"],
                U_Inicial: typeof item["Inicial"] === "number" ? excelDateToJSDate(item["Inicial"]) : item["Inicial"],
                U_Final: typeof item["Final"] === "number" ? excelDateToJSDate(item["Final"]) : item["Final"],
                U_CardCode: item["Tipo"],
                U_Nivel: item["Nivel"],
                U_NroOS: item["NroOS"]
            }));

            const response = await apiClient.post("/sales-orders/orders", { datos: datosFormateados });
            const results = response.data || [];
            exportErrorsToExcel(results);

            const ok = results.filter(r => r.status === "success").length;
            const warn = results.filter(r => r.status === "warning").length;
            const err = results.filter(r => r.status === "error").length;
            let msg = "";
            if (ok) msg += `${ok} Ordenes creadas con éxito.\n`;
            if (warn) msg += `${warn} Ordenes sin cambios.\n`;
            if (err) msg += `${err} errores en la creación.\n`;

            setImportMessage(msg);
            if (msg) alert(msg);
            setImportData([]);
            setFile(null);
            setImportError("");
            // Refrescar tabla principal
            fetchOrdenes();
        } catch (e) {
            setImportError(e?.response?.data?.error || "Error al enviar datos.");
        } finally {
            setSending(false);
        }
    };

    /* ═══════════════════ COLUMNAS TABLA PRINCIPAL ═══════════════════ */

    const mainColumns = [
        { name: "Code",            selector: r => r.Code,        sortable: true, width: "100px" },
        { name: "Tipo Inst.",      selector: r => r.TipoIns,     sortable: true, width: "100px" },
        { name: "Nro Institución", selector: r => r.NroInst,     sortable: true, width: "130px" },
        { name: "Servicio",        selector: r => r.Servicio,    sortable: true, width: "110px" },
        { name: "Cantidad",        selector: r => r.Cantidad,    sortable: true, width: "100px", right: true },
        { name: "Fecha Inicial",   selector: r => r.FechaIni,    sortable: true, width: "120px", cell: r => fmtDate(r.FechaIni) },
        { name: "Fecha Final",     selector: r => r.FechaFin,    sortable: true, width: "120px", cell: r => fmtDate(r.FechaFin) },
        { name: "Nro Lote",        selector: r => r.NroLote,     sortable: true, width: "100px" },
        { name: "Nivel Educativo", selector: r => r.NivelEduc,   sortable: true, width: "130px" },
        { name: "Nro OS",          selector: r => r.NroOS,       sortable: true, width: "100px" },
    ];

    /* ── columnas dinámicas para preview de importación ── */
    const formatHeader = (h) => h.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    const importColumns = importData[0]
        ? Object.keys(importData[0]).map(key => ({
              name: formatHeader(key),
              selector: row => row[key],
              sortable: true,
              wrap: true,
              cell: (row) => {
                  const v = row[key];
                  if (typeof v === "number" && v > 40000) return excelDateToJSDate(v);
                  if (typeof v === "number") return v.toLocaleString("es-ES");
                  return v;
              }
          }))
        : [];

    /* ── filtros ── */
    const filteredOrdenes = ordenes.filter(r =>
        Object.values(r).some(v => v?.toString().toLowerCase().includes(filterText.toLowerCase()))
    );

    const filteredImport = importData.filter(r =>
        Object.values(r).some(v => v?.toString().toLowerCase().includes(importFilter.toLowerCase()))
    );

    /* ═══════════════════════════ RENDER ═══════════════════════════ */

    return (
        <div className="container-fluid mt-3">
            <h4 className="mb-3">Órdenes de Servicios</h4>

            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

            {/* ── Barra de acciones ── */}
            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
                {/* Filtro de búsqueda */}
                <div style={{ flex: "1 1 250px" }}>
                    <input type="text" className="form-control form-control-sm"
                        placeholder="Buscar en la tabla..."
                        value={filterText} onChange={e => setFilterText(e.target.value)} />
                </div>

                {/* Rango de eliminación */}
                <div className="d-flex align-items-end gap-1">
                    <div>
                        <label className="form-label mb-0" style={{ fontSize: "0.75rem" }}>Code Desde</label>
                        <input type="text" className="form-control form-control-sm" style={{ width: 120 }}
                            value={codeIni} onChange={e => setCodeIni(e.target.value)} />
                    </div>
                    <div>
                        <label className="form-label mb-0" style={{ fontSize: "0.75rem" }}>Code Hasta</label>
                        <input type="text" className="form-control form-control-sm" style={{ width: 120 }}
                            value={codeFinal} onChange={e => setCodeFinal(e.target.value)} />
                    </div>
                    <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <Spinner size="sm" animation="border" /> : "🗑️ Eliminar Rango"}
                    </Button>
                </div>

                {/* Botones */}
                <Button variant="outline-primary" size="sm" onClick={fetchOrdenes} disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : "🔄 Actualizar"}
                </Button>
                <Button variant="success" size="sm" onClick={openImport}>
                    📥 Importar
                </Button>
            </div>

            {/* ── Tabla principal ── */}
            <DataTable
                columns={mainColumns}
                data={filteredOrdenes}
                pagination
                paginationPerPage={20}
                paginationRowsPerPageOptions={[20, 50, 100, 200]}
                fixedHeader
                fixedHeaderScrollHeight="65vh"
                highlightOnHover
                dense
                responsive
                persistTableHead
                progressPending={loading}
                noDataComponent="No hay órdenes de servicios cargadas."
            />

            {/* ═══════════════════ MODAL IMPORTACIÓN ═══════════════════ */}
            <Modal show={showImport} onHide={() => setShowImport(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Importar Órdenes de Servicios</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {importError && <Alert variant="danger">{importError}</Alert>}
                    {importMessage && <Alert variant="info">{importMessage}</Alert>}

                    <div className="mb-3">
                        <input id="fileUploadOS" type="file" className="form-control form-control-sm"
                            accept=".xlsx,.xls" onChange={handleFileChange} />
                    </div>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button variant="primary" size="sm" onClick={handleReadFile}>
                            Leer archivo
                        </Button>
                        <Button variant="success" size="sm" onClick={handleSendToBackend} disabled={sending || !importData.length}>
                            {sending ? <><Spinner size="sm" animation="border" /> Enviando...</> : "Enviar datos a SAP"}
                        </Button>
                    </div>

                    {importData.length > 0 && (
                        <>
                            <input type="text" className="form-control form-control-sm mb-2"
                                placeholder="Buscar..." value={importFilter}
                                onChange={e => setImportFilter(e.target.value)} />
                            <DataTable
                                columns={importColumns}
                                data={filteredImport}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                                fixedHeader
                                highlightOnHover
                                dense
                                responsive
                                persistTableHead
                            />
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default OrdersServices;
